import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import ErrorIcon from '@mui/icons-material/Error';
import { Rollen, ExpertStatus } from '../../../models/Rollen';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../../../services/firebaseConfig';

interface RollenSectionProps {
  rollen: Rollen[];
  loading: boolean;
  problemId: string;
  userId: string;
  onStatusChange: (rolleId: string, status: ExpertStatus) => Promise<void>;
}

interface RoleLog {
  message: string;
  timestamp: Date;
  level: string;
  role_status?: string;
}

const StatusIcon: React.FC<{ status: ExpertStatus }> = ({ status }) => {
  switch (status) {
    case 'completed':
      return <CheckCircleIcon color="success" />;
    case 'in_progress':
      return <CircularProgress size={20} color="warning" />;
    case 'error':
      return <ErrorIcon color="error" />;
    case 'pending':
      return <PendingIcon color="action" />;
    default:
      return <PendingIcon color="action" />;
  }
};

const formatLogMessage = (message: string): string => {
  const parts = message.split('global_logger - INFO - ');
  return parts.length > 1 ? parts[1] : message;
};

const RollenSection: React.FC<RollenSectionProps> = ({
  rollen,
  loading,
  problemId,
  userId,
  onStatusChange,
}) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [roleLogs, setRoleLogs] = useState<Record<string, RoleLog[]>>({});

  useEffect(() => {
    const unsubscribers = rollen.map(rolle => {
      const logRef = collection(
        db,
        'logs',
        userId,
        'problems',
        problemId,
        'roles',
        rolle.id,
        'log_entries'
      );

      const q = query(logRef, orderBy('timestamp', 'desc'));

      return onSnapshot(q, snapshot => {
        const logs: RoleLog[] = [];
        snapshot.forEach(doc => {
          const data = doc.data();
          logs.push({
            message: data.message,
            timestamp: data.timestamp.toDate(),
            level: data.level,
            role_status: data.role_status,
          });
        });

        setRoleLogs(prev => ({
          ...prev,
          [rolle.id]: logs,
        }));
      });
    });

    return () => {
      unsubscribers.forEach(unsubscribe => unsubscribe());
    };
  }, [rollen, userId, problemId]);

  if (rollen.length === 0) return null;

  return (
    <Card variant="outlined" sx={{ mt: 2 }}>
      <CardContent>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          Expert Roles
          {loading && <CircularProgress size={20} />}
        </Typography>

        {rollen.map((rolle, index) => {
          const currentLogs = roleLogs[rolle.id] || [];
          const latestLog = currentLogs[0];

          return (
            <React.Fragment key={rolle.id}>
              <Box
                sx={{
                  py: 1.5,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.02)',
                    },
                    borderRadius: 1,
                    p: 1,
                  }}
                  onClick={() => setExpandedId(expandedId === rolle.id ? null : rolle.id)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <StatusIcon status={rolle.status} />
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 500,
                        color: rolle.status === 'in_progress' ? 'warning.main' : 'text.primary',
                      }}
                    >
                      {rolle.rolle}
                    </Typography>
                    {latestLog && rolle.status !== 'completed' && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          ml: 2,
                          fontStyle: 'italic',
                          maxWidth: '400px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {formatLogMessage(latestLog.message)}
                      </Typography>
                    )}
                  </Box>
                  <IconButton size="small">
                    {expandedId === rolle.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>

                <Collapse in={expandedId === rolle.id}>
                  <Box sx={{ pl: 6, pt: 1 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 2,
                        fontStyle: 'italic',
                        borderLeft: '2px solid',
                        borderColor: 'grey.300',
                        pl: 2,
                      }}
                    >
                      {rolle.rollenbeschreibung}
                    </Typography>

                    {currentLogs.length > 0 && (
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: 'grey.50',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'grey.200',
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          gutterBottom
                          sx={{
                            color: 'primary.main',
                            fontWeight: 500,
                          }}
                        >
                          Activity Log
                        </Typography>
                        {currentLogs.map((log, i) => (
                          <Box
                            key={i}
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 2,
                              mt: 1,
                              position: 'relative',
                              '&:before': {
                                content: '""',
                                position: 'absolute',
                                left: -8,
                                top: 8,
                                width: 4,
                                height: 4,
                                bgcolor: 'primary.main',
                                borderRadius: '50%',
                              },
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                                minWidth: '90px',
                                fontFamily: 'monospace',
                              }}
                            >
                              {log.timestamp.toLocaleTimeString()}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'text.primary',
                                flex: 1,
                              }}
                            >
                              {formatLogMessage(log.message)}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </Box>
              {index < rollen.length - 1 && <Divider />}
            </React.Fragment>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default RollenSection;
